import { usePostHog } from "posthog-js/react";
import { useRouteContext } from "@tanstack/react-router";
import { useCallback } from "react";

// ── Event Names ──────────────────────────────────────────────────────────────

export const AnalyticsEvents = {
	// Auth
	AUTH_LOGIN_INITIATED: "auth_login_initiated",
	AUTH_LOGIN_COMPLETED: "auth_login_completed",
	AUTH_LOGIN_FAILED: "auth_login_failed",
	AUTH_SIGNUP_COMPLETED: "auth_signup_completed",
	AUTH_SIGNUP_FAILED: "auth_signup_failed",
	AUTH_FORGOT_PASSWORD: "auth_forgot_password",
	AUTH_LOGOUT: "auth_logout",

	// Posts
	POST_CREATED: "post_created",
	POST_EDITED: "post_edited",
	POST_DELETED: "post_deleted",
	POST_PINNED: "post_pinned",
	POST_UNPINNED: "post_unpinned",
	POST_MOVED: "post_moved",
	POST_VOTED: "post_voted",
	POST_SHARED: "post_shared",
	POST_SEARCHED: "post_searched",

	// Comments
	COMMENT_CREATED: "comment_created",
	COMMENT_REPLY_CREATED: "comment_reply_created",
	COMMENT_VOTED: "comment_voted",
	COMMENT_DELETED: "comment_deleted",

	// Events
	EVENT_CREATED: "event_created",
	EVENT_EDITED: "event_edited",
	EVENT_DELETED: "event_deleted",
	EVENT_RSVP_CONFIRMED: "event_rsvp_confirmed",
	EVENT_RSVP_CANCELLED: "event_rsvp_cancelled",

	// Courses
	COURSE_LESSON_COMPLETED: "course_lesson_completed",
	COURSE_LESSON_UNCOMPLETED: "course_lesson_uncompleted",
	COURSE_QUIZ_SUBMITTED: "course_quiz_submitted",
	COURSE_CERTIFICATE_EMITTED: "course_certificate_emitted",

	// AI
	AI_DIALOG_OPENED: "ai_dialog_opened",
	AI_MESSAGE_SENT: "ai_message_sent",
	AI_CONVERSATION_CREATED: "ai_conversation_created",

	// Navigation
	NAV_TAB_CLICKED: "nav_tab_clicked",
	NAV_SPACE_SELECTED: "nav_space_selected",
	NAV_FEED_ORDER_CHANGED: "nav_feed_order_changed",

	// Admin
	ADMIN_SPACE_CREATED: "admin_space_created",
	ADMIN_SPACE_UPDATED: "admin_space_updated",
	ADMIN_SPACE_DELETED: "admin_space_deleted",
	ADMIN_GROUP_CREATED: "admin_group_created",
	ADMIN_GROUP_UPDATED: "admin_group_updated",
	ADMIN_GROUP_DELETED: "admin_group_deleted",
	ADMIN_FEEDS_REORDERED: "admin_feeds_reordered",

	// Engagement
	POLL_VOTED: "poll_voted",
	NOTIFICATION_OPENED: "notification_opened",
	NOTIFICATION_CLICKED: "notification_clicked",

	// Profile
	PROFILE_UPDATED: "profile_updated",
} as const;

export type AnalyticsEvent =
	(typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// ── Event Properties ─────────────────────────────────────────────────────────

export interface AnalyticsEventProperties {
	// Auth
	auth_login_initiated: { source: "topnav" | "page" };
	auth_login_completed: { method: "email"; has_invite: boolean };
	auth_login_failed: { error: string };
	auth_signup_completed: { has_invite: boolean };
	auth_signup_failed: { error: string };
	auth_forgot_password: Record<string, never>;
	auth_logout: Record<string, never>;

	// Posts
	post_created: {
		space_id: string;
		has_cover: boolean;
		has_attachments: boolean;
		is_scheduled: boolean;
	};
	post_edited: { post_id: string };
	post_deleted: { post_id: string };
	post_pinned: { post_id: string; pin_type: "space" | "universal" };
	post_unpinned: { post_id: string; pin_type: "space" | "universal" };
	post_moved: { post_id: string; to_space_id: string };
	post_voted: { post_id: string; vote: "up" | "down" };
	post_shared: { post_id: string };
	post_searched: { query: string };

	// Comments
	comment_created: { parent_type: "post" | "lesson"; parent_id: string };
	comment_reply_created: { parent_comment_id: string };
	comment_voted: { comment_id: string; vote: "up" | "down" };
	comment_deleted: { comment_id: string };

	// Events
	event_created: { event_type: string; visibility: string };
	event_edited: { event_id: string };
	event_deleted: { event_id: string };
	event_rsvp_confirmed: { event_id: string };
	event_rsvp_cancelled: { event_id: string };

	// Courses
	course_lesson_completed: {
		lesson_id: string;
		course_slug: string;
	};
	course_lesson_uncompleted: {
		lesson_id: string;
		course_slug: string;
	};
	course_quiz_submitted: { lesson_id: string; course_slug: string };
	course_certificate_emitted: { course_id: string };

	// AI
	ai_dialog_opened: Record<string, never>;
	ai_message_sent: { thread_id?: string };
	ai_conversation_created: Record<string, never>;

	// Navigation
	nav_tab_clicked: { tab: string; source: "mobile" | "desktop" };
	nav_space_selected: { space_id: string };
	nav_feed_order_changed: { order: string };

	// Admin
	admin_space_created: { space_name: string; visibility: string };
	admin_space_updated: { space_id: string };
	admin_space_deleted: { space_id: string };
	admin_group_created: { group_name: string };
	admin_group_updated: { group_id: string };
	admin_group_deleted: { group_id: string };
	admin_feeds_reordered: Record<string, never>;

	// Engagement
	poll_voted: { poll_id: string };
	notification_opened: Record<string, never>;
	notification_clicked: { notification_id: string };

	// Profile
	profile_updated: Record<string, never>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAnalytics() {
	const posthog = usePostHog();
	const context = useRouteContext({ from: "__root__" });

	const track = useCallback(
		<E extends AnalyticsEvent>(
			event: E,
			properties: AnalyticsEventProperties[E],
		) => {
			posthog?.capture(event, {
				...properties,
				club_id: context.club?.id,
				club_slug: context.club?.slug,
			});
		},
		[posthog, context.club?.id, context.club?.slug],
	);

	return { track };
}
